import React, { useCallback, useRef, useState } from 'react';
import CheckmarkIcon from '@shared/icons/CheckmarkIcon';
import SchoolHeader from '@/components/SchoolHeader';
import SummaryBox from '@/components/SummaryBox';
import OutlineButton from '@/components/OutlineButton';
import CtaButton from '@/components/CtaButton';
import ErrorBanner from '@/components/ErrorBanner';
import PaymentMethodSection from '@/payment/PaymentMethodSection';
import { setDefaultPaymentMethod } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { amountBreakdownRows, formatDate, frequencyNoun, paymentMethodLabel } from '@/format';

/**
 * Web·4 — settings for an active enrollment: swap the payment method or
 * head into the turn-off warning (Web·5).
 */
const ManageScreen = ({ school, status, assignment, onTurnOff, onUpdated, assignmentSwitcher }) => {
	const [updating, setUpdating] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [selection, setSelection] = useState({ type: 'default' });
	const confirmRef = useRef(null);
	const handleBridge = useCallback((fn) => {
		confirmRef.current = fn;
	}, []);

	const studentId = status.student_id;
	const nextChargeAt = assignment.next_charge_at ?? assignment.first_charge_at;

	const handleSaveMethod = async () => {
		setError(null);
		setBusy(true);
		try {
			if (selection.type === 'new') {
				if (!confirmRef.current) {
					setError('Pick a saved card or add a new one first.');
					return;
				}
				const returnUrl = `${window.location.origin}/auto-pay${window.location.search}`;
				const { setupIntent, error: stripeError } = await confirmRef.current(returnUrl);
				if (stripeError) {
					setError(stripeError.message);
					return;
				}
				await setDefaultPaymentMethod(setupIntent.payment_method, studentId);
			} else if (selection.type === 'saved' && !selection.isDefault) {
				await setDefaultPaymentMethod(selection.pmId, studentId);
			}
			setUpdating(false);
			onUpdated();
		} catch (err) {
			setError(parseHostedError(err).message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="flex flex-col gap-[18px]">
			<SchoolHeader name={school?.name} logo={school?.logo} />

			<h1 className="text-[24px] font-extrabold text-[#16181d]">Auto-Pay settings</h1>

			{assignmentSwitcher}

			<div className="flex w-full items-center gap-[12px] rounded-[14px] bg-[#e7f4e4] px-[14px] py-[12px]">
				<div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-[#4caf50] text-white">
					<CheckmarkIcon width={13} height={13} />
				</div>
				<div className="flex flex-col gap-[2px]">
					<p className="text-[15px] font-bold text-[#1c8a00]">Auto-Pay is on</p>
					{nextChargeAt ? <p className="text-[13px] text-[#7d7d7d]">Next charge {formatDate(nextChargeAt)}</p> : null}
				</div>
			</div>

			{updating ? (
				<>
					<PaymentMethodSection
						studentId={studentId}
						stripeAccountId={status.stripe_account_id}
						defaultPm={status.default_payment_method}
						onSelectionChange={setSelection}
						onBridge={handleBridge}
						forceExpanded
					/>
					<ErrorBanner>{error}</ErrorBanner>
					<CtaButton onClick={handleSaveMethod} disabled={busy}>
						{busy ? 'Saving…' : 'Save payment method'}
					</CtaButton>
					<button
						type="button"
						onClick={() => setUpdating(false)}
						disabled={busy}
						className="w-full text-center text-[13px] font-semibold text-[#7d7d7d]"
					>
						Cancel
					</button>
				</>
			) : (
				<>
					<SummaryBox
						white
						rows={[
							{
								label: 'Payment method',
								value: (
									<span className="flex items-center gap-[10px]">
										{paymentMethodLabel(status.default_payment_method) ?? '—'}
										<button type="button" onClick={() => setUpdating(true)} className="font-semibold text-[#028afd]">
											Update
										</button>
									</span>
								),
							},
							...amountBreakdownRows(assignment),
							{ label: 'Frequency', value: frequencyNoun(assignment.frequency) },
							{ label: 'Next charge', value: nextChargeAt ? formatDate(nextChargeAt) : '—' },
						]}
					/>

					<ErrorBanner>{error}</ErrorBanner>

					<OutlineButton onClick={() => setUpdating(true)}>Update payment method</OutlineButton>

					{status.autopay_required_by_school ? (
						<p className="w-full text-center text-[13px] text-[#7d7d7d]">
							{school?.name ?? 'Your school'} requires Auto-Pay for tuition plans, so it can’t be turned off here.
						</p>
					) : (
						<OutlineButton variant="red" onClick={onTurnOff}>
							Turn off Auto-Pay
						</OutlineButton>
					)}
				</>
			)}
		</div>
	);
};

export default ManageScreen;
